import { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider, useCart } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import AssistantWidget from "@/components/AssistantWidget";
import ExitOfferPopup from "@/components/ExitOfferPopup";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Orders from "@/pages/Orders";
import Admin from "@/pages/Admin";
import Guides from "@/pages/Guides";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-voltaic-bg text-voltaic-ink px-6 text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-voltaic-cyan">System fault</p>
          <p className="text-2xl font-bold">Something overloaded the grid.</p>
          <button
            data-testid="error-reload-button"
            onClick={() => window.location.reload()}
            className="mt-2 rounded-full bg-voltaic-cyan px-6 py-2.5 font-semibold text-voltaic-bg"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// One-time offer for visitors who leave checkout with items still in the cart
function AbandonWatcher() {
  const { pathname } = useLocation();
  const { items, setExitOfferOpen } = useCart();
  useEffect(() => {
    if (pathname === "/checkout") return;
    if (
      items.length > 0 &&
      localStorage.getItem("voltaic_abandoned") === "1" &&
      !localStorage.getItem("voltaic_exit_offer")
    ) {
      localStorage.setItem("voltaic_exit_offer", "1");
      localStorage.removeItem("voltaic_abandoned");
      setExitOfferOpen(true);
    }
  }, [pathname, items.length, setExitOfferOpen]);
  return null;
}

function Shell() {
  return (
    <div className="App noise min-h-screen bg-voltaic-bg text-voltaic-ink">
      <ScrollToTop />
      <AbandonWatcher />
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/guides/:slug" element={<Guides />} />
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <CartDrawer />
      <AssistantWidget />
      <ExitOfferPopup />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <CartProvider>
        <BrowserRouter>
          <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
            <Shell />
          </ReactLenis>
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-left" />
      </CartProvider>
    </ErrorBoundary>
  );
}
